import { connect } from 'react-redux';
import { useState, useEffect, useMemo, useRef, } from 'react';

import { siteboardAction } from '../actions/defaultActions';
import { ADD_PROJECT_SCHEDULE_CONTACTS, Email, SET_ALERT, } from '../actions/types';

import PushButton from './PushButton';

// icon imports
import { Plus, X, Info, CircleCheck, } from 'lucide-react';

const ProjectContacts = ({ 
  projectId,
  onClose=null,
  onDone=null,
  showBorder=true,
  _projects,
  siteboardAction,
}) => {
  const selectedProject = _projects[projectId];

  const [emailsInput, setEmailsInput] = useState('');
  const [newContactsAddedCount, setNewContactsAddedCount] = useState(null);

  const newContactsAddedCountTimeout = useRef(null);

  // Clear new contacts added success message after a short time
  useEffect(() => {
    if(newContactsAddedCountTimeout.current !== null) {
      clearTimeout(newContactsAddedCountTimeout.current);
    }
    newContactsAddedCountTimeout.current = setTimeout(() => {
      setNewContactsAddedCount(null);
    }, 5000);
  }, [newContactsAddedCount]);

  if(!selectedProject) return <></>;

  return (
    <div className={`
      relative flex flex-col gap-2
      rounded-xl text-white 
      ${showBorder ? 'p-5 sm:p-6 border border-white/20' : ''}
      shadow-[0_8px_28px_rgba(0,0,0,0.18)]
    `}>
      <div className='flex justify-between gap-2'>
        <div className='flex flex-col gap-2'>
          <h3 className="text-xl font-bold tracking-tight text-white">
            Enter email addresses to receive the schedule when it's published
          </h3>
        </div>

        {/* Close button */}
        {typeof onClose === 'function' &&
          <button
            type="button"
            title="Close invitation form"
            aria-label="Close invitation form"
            onClick={() => onClose()}
            className="
              group flex h-9 w-9 shrink-0 items-center justify-center
              rounded-full 
              hover:-translate-y-0.5 hover:scale-[1.02] active:scale-[0.97] active:translate-y-0
              bg-[#f9a928] text-black
              focus-visible:outline-none
              focus-visible:ring-2 focus-visible:ring-[#8060ff]
            "
          >
            <X size={19} strokeWidth={2} />
          </button>
        }
      </div>
      <textarea 
        className={'project-detail-input leading-[1.4em] px-3 py-2 resize-y'}
        value={emailsInput}
        type="text"
        placeholder={'Paste or type emails here\nSeparate multiple emails with a comma or new line'}
        rows={6}
        onChange={(event) => {
          setEmailsInput(event.target.value);
        }}
      >
      </textarea>
      <div>
        <div className='flex items-center gap-2'>
          {typeof onDone === 'function' &&
            <div className='w-1/2'>
              <PushButton 
                textColor='white'
                bgColor="bg-neutral-800"
                hoverBgColor="bg-neutral-900"
                onClick={() => { onDone(); }}
                borderColor='border-white/30'
              >
                Done
              </PushButton>
            </div>
          }
          <div className='relative grow'>
            <PushButton 
              textColor='black'
              bgColor='#f9a928'
              hoverBgColor='#f79800'
              onClick={() => {
                const emails = emailsInput.split(/[\n,]+/).map(email => email.trim()).filter(email => email.length > 0);
                for(const email of emails) {
                  if(!Email.isValid(email)) {
                    siteboardAction(SET_ALERT, { message: `${email} is not a valid email. Please check that you entered it correctly, or remove it.` }); 
                    return;
                  }
                }
                if(emails.length === 0) {
                  siteboardAction(SET_ALERT, { message: 'Enter at least one email' }); 
                  return;
                }
                const onSuccess = (validOutput) => {
                  //onClose();
                  if(validOutput.contacts.length > 0) {
                    setEmailsInput('');
                  }
                  setNewContactsAddedCount(validOutput.contacts.length);
                };
                siteboardAction(ADD_PROJECT_SCHEDULE_CONTACTS, {
                  projectId: selectedProject.id,
                  emails: emails,
                }, onSuccess);
              }}
            >
              <div className='flex items-center gap-1'><Plus /> Add Emails to List</div>
            </PushButton>

            {/* Confirmation overlay */}
            <div
              className={`
                absolute top-0 left-0 overflow-hidden
                w-full h-full font-bold
                ${typeof newContactsAddedCount !== 'number' ? 'pointer-events-none' : ''}
              `}
            >
              <div
                className={`
                  absolute top-0 left-0 inset-0
                  w-full h-full
                  flex items-center justify-center
                  rounded-lg
                  transition-[opacity,transform] duration-300
                  ${typeof newContactsAddedCount === 'number' ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0' }
                  ${newContactsAddedCount === 0 ? 'bg-yellow-400' : ''}
                  ${(typeof newContactsAddedCount === 'number' && newContactsAddedCount > 0) ? 'bg-[#059669]' : ''}
                `}
              >
                {newContactsAddedCount === 0 && (
                  <div className="flex items-center gap-2 text-black">
                    <Info size={20} />
                    <span>No new contacts were added</span>
                  </div>
                )}

                {(typeof newContactsAddedCount === 'number' && newContactsAddedCount > 0) && (
                  <div className="flex items-center gap-2">
                    <CircleCheck size={20} />
                    <span>{newContactsAddedCount} new {newContactsAddedCount === 1 ? 'contact' : 'contacts'} added</span>
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

// define redux state that will be passed in as props
const mapStateToProps = state => { return {
  _projects: state.serverSynced.projects,
}};
export default connect(mapStateToProps, {siteboardAction})(ProjectContacts);
